/** 공통 파일 API 계약. 업무 화면은 첨부 연결 권한을 별도로 검사하고 파일 바이트는 인증 client로만 받는다. */
export function createCommonFileClient({ auth, area }) {
  if (!auth?.request || !['admin', 'user'].includes(area)) throw new TypeError('Invalid file client')
  const root = `/api/${area}/files`
  const groupPath = id => `${root}/${identifier(id)}`
  const itemPath = (id, sn) => {
    if (!Number.isInteger(sn) || sn < 1 || sn > 10000) throw new TypeError('Invalid file serial')
    return `${groupPath(id)}/items/${sn}`
  }
  const change = (version, reason) => ({ version: identifier(version), reason })
  return {
    list: (page = 1, size = 20) => {
      if (!Number.isInteger(page) || page < 1 || !Number.isInteger(size) || size < 1 || size > 100) throw new TypeError('Invalid file page')
      return auth.request(`${root}?page=${page}&size=${size}`)
    },
    get: id => auth.request(groupPath(id)),
    add: ({ id, version, reason, links = [], files = [] }) => {
      if (!Array.isArray(links) || !Array.isArray(files) || links.length + files.length < 1 || links.length + files.length > 10) throw new TypeError('Invalid file batch')
      let size = 0
      for (const file of files) {
        if (!(file instanceof Blob) || typeof file.name !== 'string' || file.size < 1 || file.size > 20 * 1024 * 1024) throw new TypeError('Invalid upload')
        size += file.size
      }
      if (size > 100 * 1024 * 1024) throw new TypeError('File batch too large')
      const input = id == null ? { reason, links } : { ...change(version, reason), links }
      if (id == null && version != null) throw new TypeError('Unexpected file version')
      const body = new FormData()
      body.append('request', new Blob([JSON.stringify(input)], { type: 'application/json' }))
      for (const file of files) body.append('files', file, file.name)
      return auth.request(id == null ? root : `${groupPath(id)}/items`, { method: 'POST', body })
    },
    archive: (id, version, reason) => auth.request(`${groupPath(id)}/archive`, { method: 'POST', body: change(version, reason) }),
    archiveItem: (id, sn, version, reason) => auth.request(`${itemPath(id, sn)}/archive`, { method: 'POST', body: change(version, reason) }),
    download: (id, sn) => auth.request(`${itemPath(id, sn)}/content`, { responseType: 'blob' }),
  }
}

function identifier(value) {
  // bigint 키/버전은 JSON 문자열로 유지하며 이미 정밀도를 잃은 number는 허용하지 않는다.
  if (typeof value !== 'string' || !/^[1-9]\d{0,18}$/.test(value) || BigInt(value) > 9223372036854775807n) throw new TypeError('Invalid file identifier')
  return value
}

/** 서버가 승인한 외부 미디어와 인증 다운로드를 구분한다. 저장소 경로나 임의 contentPath로 요청하지 않는다. */
export function fileDisplay(item) {
  const result = { name: typeof item?.name === 'string' ? item.name : '', kind: 'DOCUMENT', url: null, download: false, available: false }
  if (item?.available !== true) return result
  if (item.sourceKind === 'FILE') return { ...result, kind: item.mediaKind === 'IMAGE' ? 'IMAGE' : 'DOCUMENT', download: true, available: true }
  if (item.sourceKind !== 'URL' || typeof item.mediaUrl !== 'string' || /[\s\\]/.test(item.mediaUrl)
    || [...item.mediaUrl].some(char => char.codePointAt(0) < 32 || char.codePointAt(0) === 127)) return result
  try {
    const url = new URL(item.mediaUrl)
    if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) return result
    return { ...result, kind: ['IMAGE', 'VIDEO', 'AUDIO'].includes(item.mediaKind) ? item.mediaKind : 'DOCUMENT', url: url.href, available: true }
  } catch { return result }
}
